/** ErrorBoundary — Blueprint Part 9.
 *
 *  Wraps the AppShell (and the public surfaces too) so an exception in any
 *  child renders a friendly recovery card instead of a blank screen.
 *  Logs to console (and reports via window.dispatchEvent for future Sentry).
 */
import { Component } from "react";
import { Button, EmptyState } from "@/design";
import { color } from "@/design/tokens";

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // Surface to console + a custom window event so we can later wire Sentry
    // without changing the boundary itself.
    console.error("[ws:boundary] caught:", error, info?.componentStack);
    try {
      window.dispatchEvent(new CustomEvent("ws:client-error", {
        detail: { error: String(error), stack: info?.componentStack },
      }));
    } catch (e) { /* ignore */ }
  }
  reset = () => {
    this.setState({ hasError: false, error: null });
    // Soft re-route so route guards re-evaluate
    try { window.location.assign("/app/dashboard"); } catch (e) { /* ignore */ }
  };
  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary" style={{
          minHeight: "60vh", display: "flex", alignItems: "center",
          justifyContent: "center", padding: 24,
          background: this.props.tone === "dark" ? color.navy900 : color.paper,
        }}>
          <div style={{ maxWidth: 480 }}>
            <EmptyState
              tone={this.props.tone || "dark"}
              title="Something hiccupped on our side."
              helper="The crash is logged. You can refresh, or jump back to your dashboard. If this keeps happening, drop us a line at support@wavescorepro.com."
              action={<Button data-testid="error-boundary-reset" variant="primary" audience="station" onClick={this.reset}>Back to dashboard →</Button>}
            />
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
